import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { 
  Eye, 
  EyeOff, 
  ArrowRight,
  UserPlus,
  Building2,
  Lock,
  Mail,
  User,
  Shield
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { QRCodeCanvas } from 'qrcode.react'; // Updated import
import { Select } from 'antd';


const { Option } = Select;

const AVAILABLE_DESIGNATIONS = [
  { value: "CFP", label: "CFP®️" },        // Certified Financial Planner
  { value: "CFA", label: "CFA" },          // Chartered Financial Analyst
  { value: "ChFC", label: "ChFC" },        // Chartered Financial Consultant
  { value: "CIMA", label: "CIMA®️" },      // Certified Investment Management Analyst
  { value: "CPWA", label: "CPWA®️" },      // Certified Private Wealth Advisor
  { value: "CEPA", label: "CEPA®️" },      // Certified Exit Planning Advisor
  { value: "CLU", label: "CLU®️" },        // Chartered Life Underwriter
  { value: "RICP", label: "RICP®️" },      // Retirement Income Certified Professional
  { value: "AIF", label: "AIF®️" },        // Accredited Investment Fiduciary
  { value: "CRPC", label: "CRPC®️" },      // Chartered Retirement Planning Counselor


  // Additional retirement/401(k)-focused designations
  { value: "QKA", label: "QKA®️" },        // Qualified 401(k) Administrator (ASPPA)
  { value: "QPA", label: "QPA®️" },        // Qualified Pension Administrator (ASPPA)
  { value: "TGPC", label: "TGPC®️" },      // Tax-Exempt & Governmental Plan Consultant (ASPPA)
  { value: "CPC", label: "CPC®️" },        // Certified Pension Consultant (ASPPA)
  { value: "CPFA", label: "CPFA®️" },      // Certified Plan Fiduciary Advisor (NAPA)
  { value: "NQPA", label: "NQPA®️" },      // Nonqualified Plan Advisor (NAPA)
  { value: "PPC", label: "PPC™" },         // Professional Plan Consultant (fi360)
  { value: "AIFA", label: "AIFA®️" },      // Accredited Investment Fiduciary Analyst
  { value: "ERPA", label: "ERPA" },        // Enrolled Retirement Plan Agent (IRS)
  { value: "CRPS", label: "CRPS®️" },      // Chartered Retirement Plans Specialist
  { value: "FCA", label: "FCA" },          // Fellow, Conference of Consulting Actuaries
  { value: "ARA", label: "ARA Member" },   // Member of the American Retirement Association

  { value: "MBA", label: "MBA" },
  { value: "JD", label: "JD" },
  { value: "MD", label: "MD" },
  { value: "PhD", label: "PhD" },
];


const selectStyles = {
  width: '100%',
  height: '42px', // Match input height
  borderRadius: '0.5rem', // Match input border radius
};

const SignupForm = () => {
  // Form data state
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    companyName: '',
    advisor1Name: '',
    advisor1Designation: '',
    advisor2Name: '',
    advisor2Designation: '',
    advisor3Name: '',
    advisor3Designation: ''
  });

  // Other state variables
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordStrength, setPasswordStrength] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);

  // TOTP state
  const [totpUri, setTotpUri] = useState('');
  const [showTotpModal, setShowTotpModal] = useState(false);

  const navigate = useNavigate();

  // Function to check password strength
  const checkPasswordStrength = (password) => {
    setPasswordStrength({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      lowercase: /[a-z]/.test(password),
      number: /[0-9]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
    });
  };

  // Function to determine if password is strong
  const isPasswordStrong = () => {
    return Object.values(passwordStrength).every(criteria => criteria);
  };

  // Handle input changes for most fields
  const handleChange = (e) => {
    const { name, value } = e.target;

    // If password field, check strength
    if (name === 'password') {
      checkPasswordStrength(value);
    }

    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Handle input changes specifically for companyName to trim spaces
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // Special handling for advisor names and company name
    if (name.includes('advisor') || 
        name.includes('Name') ||
        name === 'companyName') {
      setFormData(prev => ({
        ...prev,
        [name]: value  // Don't trim spaces for these fields
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value.trim()  // Trim spaces for other fields
      }));
    }
  };

  // Generate password validation message
  const getPasswordValidationMessage = (password) => {
    if (!password) return '';
    const missing = [];
    if (password.length < 8) missing.push('8 characters');
    if (!/[A-Z]/.test(password)) missing.push('uppercase letter');
    if (!/[a-z]/.test(password)) missing.push('lowercase letter');
    if (!/\d/.test(password)) missing.push('number');
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) missing.push('special character');

    if (missing.length === 0) return '';
    if (missing.length === 1) {
      return `Password must contain at least one ${missing[0]}`;
    }
    const lastItem = missing.pop();
    return `Password must contain at least one ${missing.join(', ')} and one ${lastItem}`;
  };

  // Generate password match message
  const getPasswordMatchMessage = () => {
    if (confirmPasswordTouched && formData.password !== formData.confirmPassword) {
      return 'Passwords must match';
    }
    return '';
  };

  // Email validation regex
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  // Generate email validation message
  const getEmailValidationMessage = (email) => {
    if (!email) return '';
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Define required fields - only these fields are required
    const requiredFields = [
      'email',
      'password',
      'confirmPassword',
      'firstName',
      'lastName'
      // advisor1Name will be auto-populated from firstName + lastName
      // Company info and advisor designations are now optional
    ];

    // Validate only required fields
    const emptyRequiredFields = requiredFields.filter(field => !formData[field]);
    if (emptyRequiredFields.length > 0) {
      setError(`Please fill in all required fields: ${emptyRequiredFields.map(key => key).join(', ')}`);
      return;
    }

    // Validate passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Validate email format
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check password strength
    if (!isPasswordStrong()) {
      setError('Please ensure your password meets all strength requirements');
      return;
    }

    // Check terms acceptance
    if (!acceptedTerms) {
      setError('Please accept the Terms and Privacy Policy to continue');
      return;
    }

    setIsLoading(true);

    try {
      // Auto-populate advisor1Name from firstName and lastName
      const advisorName = `${formData.firstName} ${formData.lastName}`.trim();
      
      const submitData = {
        email: formData.email,
        password: formData.password,
        first_name: formData.firstName,
        last_name: formData.lastName,
        company_name: formData.companyName || '',
        company_phone: '',
        company_email: '',
        company_website: '',
        company_disclaimer: '',
        company_address: '',
        advisor1_name: advisorName, // Auto-populated from first name + last name
        advisor1_designation: formData.advisor1Designation || '',
        advisor2_name: formData.advisor2Name?.trim() || '',
        advisor2_designation: formData.advisor2Designation?.trim() || '',
        advisor3_name: formData.advisor3Name?.trim() || '',
        advisor3_designation: formData.advisor3Designation?.trim() || ''
      };
      
      // Create Stripe checkout session instead of direct signup
      const response = await fetch('https://plansync-backend-234288438538.us-central1.run.app/api/stripe/create-checkout-session', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(submitData),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.detail || 'Failed to create checkout session');
      }

      // Redirect to Stripe checkout
      window.location.href = data.checkout_url;
      
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this component for section headers
  const SectionHeader = ({ title, required, icon: Icon }) => (
    <div className="flex items-center gap-3 mb-4">
      {Icon && <Icon className="w-5 h-5 text-sky-600" />}
      <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-700 to-gray-900 bg-clip-text text-transparent">{title}</h3>
      {required && (
        <span className="inline-flex items-center px-2.5 py-0.5 bg-sky-100 text-sky-700 text-xs font-medium rounded-full">
          Required
        </span>
      )}
    </div>
  );

  return (
    <>
      {/* Signup Form */}
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-12 px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.4, ease: "easeOut" }}
          className="relative bg-white/95 backdrop-blur-md rounded-2xl shadow-lg shadow-gray-400/20 p-8 w-full max-w-md border border-slate-200/50 my-8"
        >
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.4 }}
            className="text-center mb-8"
          >
            <h1 className="text-3xl font-bold bg-gradient-to-r from-gray-800 to-sky-600 bg-clip-text text-transparent mb-2">Create Account</h1>
            <p className="text-gray-500 text-sm">Join PlanSync AI today</p>
          </motion.div>

          {/* Error Message */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 mb-6 rounded-xl flex items-start space-x-2"
                role="alert"
              >
                <svg className="w-5 h-5 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm">{error}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Signup Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Account Information Section */}
            <div className="space-y-4">
              <SectionHeader title="Account Information" required icon={User} />
              
              <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="relative group"
              >
                <Mail className="h-5 w-5 text-gray-400 absolute top-3.5 left-3 transition-colors group-hover:text-sky-500" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  onBlur={() => setEmailTouched(true)}
                  className="w-full pl-10 pr-3 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
                  placeholder="Email address"
                  required
                />
                {emailTouched && !emailRegex.test(formData.email) && (
                  <p className="mt-1 text-sm text-red-600 pl-10">
                    {getEmailValidationMessage(formData.email)}
                  </p>
                )}
              </motion.div>

              <div className="flex space-x-3">
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex-1"
                >
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="First Name"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
                    required
                  />
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="flex-1"
                >
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Last Name"
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
                    required
                  />
                </motion.div>
              </div>

              {/* Company Name - moved to Account Information section */}
              <motion.div
                whileHover={{ scale: 1.01 }}
                whileTap={{ scale: 0.99 }}
                className="relative group"
              >
                <Building2 className="h-5 w-5 text-gray-400 absolute top-3.5 left-3 transition-colors group-hover:text-sky-500" />
                <input
                  type="text"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleInputChange}
                  placeholder="Company/Firm Name (Optional)"
                  className="w-full pl-10 pr-3 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
                />
              </motion.div>
            </div>

            {/* Advisor Information hidden - auto-populated from first and last name */}
            <input type="hidden" name="advisor1Designation" value="" />

            {/* Password Section */}
            <div className="space-y-4">
              <SectionHeader title="Password" required icon={Lock} />
              
              <div className="space-y-4">
                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="relative group"
                >
                  <Lock className="h-5 w-5 text-gray-400 absolute top-3.5 left-3 transition-colors group-hover:text-sky-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    placeholder="Password"
                    className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
                    required
                    onBlur={() => setPasswordTouched(true)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className="relative group"
                >
                  <Lock className="h-5 w-5 text-gray-400 absolute top-3.5 left-3 transition-colors group-hover:text-sky-500" />
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    placeholder="Confirm Password"
                    className="w-full pl-10 pr-10 py-3 bg-gray-50 border border-gray-200 hover:border-gray-300 focus:border-sky-500 focus:bg-white text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-sky-500/20 rounded-xl transition-all duration-200"
                    required
                    onBlur={() => setConfirmPasswordTouched(true)}
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-5 w-5" />
                    ) : (
                      <Eye className="h-5 w-5" />
                    )}
                  </button>
                </motion.div>
              </div>
            </div>


            {/* Terms and Submit Section */}
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-xl">
                <div className="flex items-start space-x-3">
                  <input
                    type="checkbox"
                    id="terms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500 cursor-pointer"
                    required
                  />
                  <label htmlFor="terms" className="text-gray-600 text-sm">
                    I have read and agree to the{' '}
                    <a href="https://drive.google.com/file/d/1AACdaA-dwVBOkH6GQ4msDcML35fe7PSB/view?usp=sharing" className="text-sky-600 hover:text-sky-700 font-medium transition-colors" target="_blank" rel="noopener noreferrer">
                      Terms of Service
                    </a>
                    {' '}and{' '}
                    <a href="https://drive.google.com/file/d/1T1YtQA8wW7MCw-RT-7wdJhWtjPqr885R/view?usp=sharing" className="text-sky-600 hover:text-sky-700 font-medium transition-colors" target="_blank" rel="noopener noreferrer">
                      Privacy Policy
                    </a>
                  </label>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                type="submit"
                className={`w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-3.5 rounded-xl flex items-center justify-center space-x-2 font-medium shadow-lg shadow-sky-500/25 transition-all duration-200 ${
                  isLoading || !acceptedTerms ? 'opacity-70 cursor-not-allowed' : 'hover:shadow-xl hover:shadow-sky-500/30'
                }`}
                disabled={isLoading || !acceptedTerms}
              >
                {isLoading ? (
                  <>
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                      className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full"
                    />
                    <span>Creating Account...</span>
                  </>
                ) : (
                  <>
                    <span>Create Account</span>
                    <ArrowRight className="h-5 w-5" />
                  </>
                )}
              </motion.button>
            </div>
          </form>

          {/* Link to Login */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
            className="mt-8 text-center pt-6 border-t border-gray-100"
          >
            <p className="text-gray-500 text-sm mb-2">Already have an account?</p>
            <Link 
              to="/login" 
              className="inline-flex items-center space-x-1 text-sky-600 hover:text-sky-700 font-medium transition-colors duration-200 group"
            >
              <span>Sign in instead</span>
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5, duration: 0.4 }}
            className="absolute -top-4 -right-4 w-24 h-24 bg-gradient-to-br from-sky-400/20 to-blue-600/20 rounded-full blur-2xl"
          />
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.4 }}
            className="absolute -bottom-4 -left-4 w-32 h-32 bg-gradient-to-tr from-blue-400/20 to-sky-600/20 rounded-full blur-2xl"
          />
        </motion.div>
      </div>

      {/* TOTP Setup Modal */}
      {showTotpModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/50 backdrop-blur-sm z-50">
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white/95 backdrop-blur-md p-8 rounded-2xl shadow-lg shadow-gray-400/20 max-w-md mx-auto border border-slate-200/50"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-400 to-green-600 rounded-xl flex items-center justify-center">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h2 className="text-2xl font-bold bg-gradient-to-r from-gray-800 to-sky-600 bg-clip-text text-transparent">Two-Factor Authentication</h2>
            </div>
            <p className="mb-4">
              Scan the QR code below with your authenticator app (e.g., Google Authenticator, Authy).
            </p>
            <div className="flex justify-center mb-4">
              <QRCodeCanvas value={totpUri} size={256} /> {/* Updated component */}
            </div>
            <p className="mb-4">
              After scanning, enter the code generated by your authenticator app when prompted during login.
            </p>
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setShowTotpModal(false);
                navigate('/login');
              }}
              className="w-full bg-gradient-to-r from-sky-500 to-blue-600 text-white py-3.5 rounded-xl flex items-center justify-center space-x-2 font-medium shadow-lg shadow-sky-500/25 hover:shadow-xl hover:shadow-sky-500/30 transition-all duration-200"
            >
              <span>Proceed to Login</span>
              <ArrowRight className="h-5 w-5" />
            </motion.button>
          </motion.div>
        </div>
      )}
    </>
  );
};

export default SignupForm;
